(() => {
  const initCareSwitchTabs = (root) => {
    if (!root || root.dataset.careSwitchTabsReady === 'true') return;
    root.dataset.careSwitchTabsReady = 'true';

    const tabs = Array.from(root.querySelectorAll('[data-care-tab]'));
    const tabsNav = root.querySelector('[data-care-tabs-nav]');
    const tabsViewport = root.querySelector('[data-care-tabs-viewport]');
    const tabsTrack = root.querySelector('[data-care-tabs-track]');
    const prevBtn = root.querySelector('[data-care-tabs-prev]');
    const nextBtn = root.querySelector('[data-care-tabs-next]');
    const configNode = root.querySelector('[data-care-tabs-config]');
    const optionsNode = root.querySelector('[data-care-product-options]');
    const variantsNode = root.querySelector('[data-care-product-variants]');
    const isProductPage = root.dataset.isProductPage === 'true';
    const optionName = (root.dataset.optionName || '').trim();
    const hiddenRecords = new Map();
    let configs = [];
    let productOptions = [];
    let productVariants = [];
    let activeTab = null;
    let tabsMinX = 0;
    let tabsCurrentX = 0;
    let suppressClick = false;

    const drag = {
      pointerId: null,
      startX: 0,
      lastX: 0,
      startTranslate: 0,
      moved: false
    };

    if (!tabs.length || !configNode) return;

    try {
      configs = JSON.parse(configNode.textContent || '[]');
    } catch (error) {
      configs = [];
    }

    try {
      productOptions = optionsNode ? JSON.parse(optionsNode.textContent || '[]') : [];
    } catch (error) {
      productOptions = [];
    }

    try {
      productVariants = variantsNode ? JSON.parse(variantsNode.textContent || '[]') : [];
    } catch (error) {
      productVariants = [];
    }

    const normalizeValue = (text) => (text || '').replace(/\u00a0/g, ' ').trim();

    const normalizeLine = (text) =>
      (text || '')
        .replace(/\u00a0/g, ' ')
        .replace(/[\u200b-\u200d\ufeff]/g, '')
        .replace(/^\s+/, '')
        .replace(/^[-*]\s+/, '')
        .trim();

    const parseSelectors = (html) => {
      const container = document.createElement('div');
      const selectors = [];
      container.innerHTML = html || '';

      const getLines = (node) => {
        const clone = node.cloneNode(true);
        Array.from(clone.querySelectorAll('br')).forEach((br) => br.replaceWith('\n'));
        return clone.textContent
          .split(/\r?\n/)
          .map(normalizeLine)
          .filter(Boolean);
      };

      Array.from(container.children).forEach((child) => {
        const tagName = child.tagName.toLowerCase();
        if (tagName === 'ul' || tagName === 'ol') {
          Array.from(child.querySelectorAll('li')).forEach((item) => selectors.push(...getLines(item)));
          return;
        }
        selectors.push(...getLines(child));
      });

      return selectors;
    };

    const getConfig = (tabId) => configs.find((item) => item.id === tabId);

    const escapeAttributeValue = (value) => String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');

    const getShopifySectionKey = (selector) => {
      const normalizedSelector = normalizeLine(selector).replace(/^#/, '');
      const match = normalizedSelector.match(/^shopify-section-template--[^\s]+__(.+)$/);
      return match ? match[1] : '';
    };

    const getElements = (selectors) => {
      const elements = [];
      selectors.forEach((selector) => {
        const sectionKey = getShopifySectionKey(selector);
        const selectorCandidates = [selector];
        if (sectionKey) {
          selectorCandidates.push(`[id$="__${escapeAttributeValue(sectionKey)}"]`);
        }

        selectorCandidates.forEach((selectorCandidate) => {
          try {
            document.querySelectorAll(selectorCandidate).forEach((element) => {
              if (!elements.includes(element)) elements.push(element);
            });
          } catch (error) {
            if (!sectionKey || selectorCandidate !== selector) {
              console.warn('Invalid care-switch-tabs selector:', selectorCandidate);
            }
          }
        });
      });
      return elements;
    };

    const restoreHiddenElements = () => {
      hiddenRecords.forEach((record, element) => {
        if (record.hiddenAttribute) {
          element.setAttribute('hidden', '');
        } else {
          element.removeAttribute('hidden');
        }
        element.style.display = record.display;
      });
      hiddenRecords.clear();
    };

    const hideElementsForTab = (tabId) => {
      const config = getConfig(tabId);
      if (!config) return;

      getElements(parseSelectors(config.selectors)).forEach((element) => {
        if (!hiddenRecords.has(element)) {
          hiddenRecords.set(element, {
            display: element.style.display,
            hiddenAttribute: element.hasAttribute('hidden')
          });
        }
        element.hidden = true;
        element.style.display = 'none';
      });
    };

    const clampTabsX = (x) => Math.max(tabsMinX, Math.min(0, x));

    const updateOverflowState = () => {
      if (!tabsNav) return;
      const hasOverflow = tabsMinX < 0;
      tabsNav.classList.toggle('is-overflowing', hasOverflow);
      tabsNav.classList.toggle('has-overflow-left', hasOverflow && tabsCurrentX < -1);
      tabsNav.classList.toggle('has-overflow-right', hasOverflow && tabsCurrentX > tabsMinX + 1);
    };

    const setTabsX = (x, animate = false) => {
      if (!tabsTrack) return;
      tabsCurrentX = clampTabsX(x);
      tabsTrack.style.transition = animate ? 'transform 320ms ease' : 'none';
      tabsTrack.style.transform = `translate3d(${tabsCurrentX}px, 0, 0)`;
      updateOverflowState();
    };

    const refreshBounds = () => {
      if (!tabsViewport || !tabsTrack) return;
      const overflow = tabsTrack.scrollWidth - tabsViewport.clientWidth;
      tabsMinX = overflow > 1 ? -overflow : 0;
      setTabsX(tabsMinX === 0 ? 0 : tabsCurrentX);
    };

    const scheduleRefresh = () => {
      refreshBounds();
      requestAnimationFrame(refreshBounds);
      window.setTimeout(refreshBounds, 80);
    };

    const ensureTabVisible = (tab, animate = true) => {
      if (!tab || !tabsViewport || !tabsTrack || tabsMinX === 0) return;
      const viewportRect = tabsViewport.getBoundingClientRect();
      const tabRect = tab.getBoundingClientRect();
      const styles = window.getComputedStyle(tabsNav || tabsViewport);
      const edgeSize = parseFloat(styles.getPropertyValue('--care-tabs-edge-size')) || 60;
      const safeInset = edgeSize + 4;
      let nextX = tabsCurrentX;

      if (tabRect.left < viewportRect.left + safeInset) {
        nextX += viewportRect.left + safeInset - tabRect.left;
      } else if (tabRect.right > viewportRect.right - safeInset) {
        nextX -= tabRect.right - (viewportRect.right - safeInset);
      }

      if (Math.abs(nextX - tabsCurrentX) > 0.5) {
        setTabsX(nextX, animate);
      }
    };

    const clearActiveTab = () => {
      activeTab = null;
      tabs.forEach((item, index) => {
        item.classList.remove('is-active');
        item.setAttribute('aria-selected', 'false');
        item.tabIndex = index === 0 ? 0 : -1;
      });
      restoreHiddenElements();
    };

    const setActiveTab = (tab, options = {}) => {
      if (!tab) {
        clearActiveTab();
        return;
      }
      if (activeTab === tab) return;
      activeTab = tab;
      tabs.forEach((item) => {
        const active = item === tab;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', active ? 'true' : 'false');
        item.tabIndex = active ? 0 : -1;
      });

      restoreHiddenElements();
      hideElementsForTab(tab.dataset.careTabId);
      ensureTabVisible(tab, options.animate !== false);
    };

    const getOptionIndex = () => {
      const dataIndex = root.dataset.optionIndex;
      if (dataIndex !== '' && dataIndex !== undefined && dataIndex !== null) {
        const parsedIndex = Number(dataIndex);
        if (Number.isInteger(parsedIndex) && parsedIndex >= 0) return parsedIndex;
      }

      if (!optionName) return -1;
      return productOptions.findIndex((name) => normalizeValue(name) === optionName);
    };

    const getVariantById = (variantId) => {
      if (!variantId) return null;
      const selectedVariantId = String(variantId);
      const productVariant = productVariants.find((item) => String(item.id) === String(selectedVariantId));
      if (productVariant) return productVariant;

      const variantJson = document.querySelector('variant-selects script[type="application/json"], variant-radios script[type="application/json"], [data-product-json]');
      if (variantJson) {
        try {
          const data = JSON.parse(variantJson.textContent || '{}');
          const variants = Array.isArray(data) ? data : data.variants || [];
          return variants.find((item) => String(item.id) === String(selectedVariantId)) || null;
        } catch (error) {
          return null;
        }
      }

      return null;
    };

    const getOptionValueFromVariant = (variant) => {
      if (!variant) return '';
      const index = getOptionIndex();
      if (index < 0) return '';

      if (Array.isArray(variant.options) && variant.options[index] !== undefined) {
        return normalizeValue(variant.options[index]);
      }

      const keyedOption = variant[`option${index + 1}`];
      return keyedOption ? normalizeValue(keyedOption) : '';
    };

    const getCurrentVariantInput = () => {
      const checkedVariantInput = document.querySelector('form[action*="/cart/add"] input[name="id"]:checked');
      if (checkedVariantInput) return checkedVariantInput;

      const enabledInput = Array.from(document.querySelectorAll('form[action*="/cart/add"] [name="id"]'))
        .find((input) => !input.disabled && input.value);
      if (enabledInput) return enabledInput;

      return document.querySelector('form[action*="/cart/add"] [name="id"]');
    };

    const getCurrentOptionValue = () => {
      if (!isProductPage) return '';
      const variantInput = getCurrentVariantInput();
      if (!variantInput) return normalizeValue(root.dataset.currentOptionValue);

      return getOptionValueFromVariant(getVariantById(variantInput.value)) || normalizeValue(root.dataset.currentOptionValue);
    };

    const setActiveByOptionValue = (optionValue, options = {}) => {
      const normalizedOptionValue = normalizeValue(optionValue);
      const matchedTab = normalizedOptionValue
        ? tabs.find((tab) => normalizeValue(tab.dataset.careOptionValue) === normalizedOptionValue)
        : null;
      setActiveTab(matchedTab || null, options);
    };

    const syncFromProductOption = (variantOrValue, options = {}) => {
      if (!isProductPage) return;
      const optionValue = typeof variantOrValue === 'object'
        ? getOptionValueFromVariant(variantOrValue)
        : normalizeValue(variantOrValue);
      setActiveByOptionValue(optionValue || getCurrentOptionValue(), options);
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', (event) => {
        if (suppressClick) {
          event.preventDefault();
          return;
        }
        setActiveTab(tab);
      });

      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const nextIndex = (index + direction + tabs.length) % tabs.length;
        tabs[nextIndex].focus();
        setActiveTab(tabs[nextIndex]);
      });
    });

    if (tabsTrack && tabsViewport) {
      tabsTrack.addEventListener('pointerdown', (event) => {
        if (tabsMinX === 0) return;
        drag.pointerId = event.pointerId;
        drag.startX = event.clientX;
        drag.lastX = event.clientX;
        drag.startTranslate = tabsCurrentX;
        drag.moved = false;
        tabsTrack.setPointerCapture(event.pointerId);
        tabsTrack.style.transition = 'none';
      });

      tabsTrack.addEventListener('pointermove', (event) => {
        if (drag.pointerId !== event.pointerId) return;
        drag.lastX = event.clientX;
        const delta = drag.lastX - drag.startX;
        if (Math.abs(delta) > 4) {
          drag.moved = true;
          suppressClick = true;
        }
        setTabsX(drag.startTranslate + delta);
      });

      tabsTrack.addEventListener('pointerup', (event) => {
        if (drag.pointerId !== event.pointerId) return;
        drag.pointerId = null;
        if (drag.moved) window.setTimeout(() => { suppressClick = false; }, 80);
      });

      tabsTrack.addEventListener('pointercancel', () => {
        drag.pointerId = null;
        suppressClick = false;
      });

      tabsViewport.addEventListener('wheel', (event) => {
        if (tabsMinX === 0 || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
        event.preventDefault();
        setTabsX(tabsCurrentX - event.deltaX);
      }, { passive: false });
    }

    if (prevBtn) prevBtn.addEventListener('click', () => setTabsX(tabsCurrentX + tabsViewport.clientWidth * 0.8, true));
    if (nextBtn) nextBtn.addEventListener('click', () => setTabsX(tabsCurrentX - tabsViewport.clientWidth * 0.8, true));

    document.addEventListener('change', (event) => {
      if (!isProductPage) return;
      if (!event.target.matches('form[action*="/cart/add"] [name="id"]')) return;
      window.setTimeout(() => syncFromProductOption(), 0);
    });

    document.addEventListener('change', (event) => {
      if (!isProductPage) return;
      if (!event.target.matches('variant-selects select, variant-selects input, variant-radios select, variant-radios input')) return;
      window.setTimeout(() => syncFromProductOption(), 0);
    });

    document.addEventListener('variant:change', (event) => {
      if (!isProductPage) return;
      syncFromProductOption(event.detail?.variant || '');
    });

    document.addEventListener('product:variant-change', (event) => {
      if (!isProductPage) return;
      syncFromProductOption(event.detail?.variant || '');
    });

    window.addEventListener('resize', scheduleRefresh, { passive: true });
    if (isProductPage) {
      syncFromProductOption('', { animate: false });
    } else {
      setActiveTab(tabs[0], { animate: false });
    }
    scheduleRefresh();
  };

  const initAllCareSwitchTabs = () => {
    document.querySelectorAll('[data-care-switch-tabs]').forEach(initCareSwitchTabs);
  };

  if (window.__careSwitchTabsInit) {
    window.__careSwitchTabsInit();
    return;
  }

  window.__careSwitchTabsInit = initAllCareSwitchTabs;
  initAllCareSwitchTabs();

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-care-switch-tabs]').forEach(initCareSwitchTabs);
  });
})();
